import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  MenuItem,
  Select,
  FormHelperText,
  InputLabel
} from '@mui/material';
import PropTypes from 'prop-types';
import SoftBox from 'components/SoftBox';
import SoftButton from 'components/SoftButton';
import SoftTypography from 'components/SoftTypography';
import SoftInput from 'components/SoftInput';
import { partnerService } from 'services/partnerService';
import iaasService from 'services/iaasService';
import saasService from 'services/saasService';
import { AuthContext } from 'context';

const ADMIN_IDS = [1, 2, 3];

const isAdminUser = (user) => Boolean(user?.isAdmin) || ADMIN_IDS.includes(user?.id);

const ServiceAssignDialog = ({ open, onClose, serviceId, serviceType, onAssigned }) => {
  const { user } = useContext(AuthContext);
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [endCustomerPrice, setEndCustomerPrice] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCustomers = async () => {
      setIsLoading(true);
      try {
        const customersData = await partnerService.getCustomers(user?.id);
        setCustomers(customersData);
        setError('');
      } catch (error) {
        console.error('Fehler beim Laden der Kunden:', error);
        setError('Kunden konnten nicht geladen werden');
      } finally {
        setIsLoading(false);
      }
    };

    if (open) {
      fetchCustomers();
    }
  }, [open, user?.id]);

  const formatCustomerLabel = (customer) => {
    const displayName = customer.company
      || [customer.Vorname, customer.Nachname].filter(Boolean).join(' ')
      || customer.username
      || `Webuser ${customer.id}`;
    const email = customer.email ? ` (${customer.email})` : '';
    const partnerInfo = isAdminUser(user)
      ? ` - Partner: ${customer.partnerId || 'keiner'}`
      : '';

    return `${displayName}${email}${partnerInfo}`;
  };

  const handleAssign = async () => {
    if (!selectedCustomerId) {
      setError('Bitte wählen Sie einen Kunden aus');
      return;
    }

    setIsLoading(true);
    try {
      let response;
      if (serviceType === 'iaas') {
        response = await iaasService.assignServiceToCustomer(serviceId, selectedCustomerId, endCustomerPrice);
      } else {
        response = await saasService.assignServiceToCustomer(serviceId, selectedCustomerId, endCustomerPrice);
      }

      if (response.success) {
        onAssigned(response.service);
      } else {
        setError('Beim Zuweisen ist ein Fehler aufgetreten');
      }
    } catch (error) {
      console.error('Fehler beim Zuweisen des Services:', error);
      setError('Beim Zuweisen ist ein Fehler aufgetreten');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <SoftTypography variant="h5" fontWeight="medium">
          Service einem Kunden zuweisen
        </SoftTypography>
      </DialogTitle>

      <DialogContent>
        <SoftBox py={2}>
          <SoftTypography variant="body2" mb={3}>
            Weisen Sie diesen Service einem Ihrer Kunden zu. Der Kunde wird dann Zugriff auf den Service haben und kann ihn verwalten.
          </SoftTypography>

          <FormControl fullWidth error={!!error}>
            <InputLabel id="customer-select-label">Kunde auswählen</InputLabel>
            <Select
              labelId="customer-select-label"
              id="customer-select"
              value={selectedCustomerId}
              onChange={(e) => {
                setSelectedCustomerId(e.target.value);
                setError('');
              }}
              disabled={isLoading}
              label="Kunde auswählen"
            >
              {customers.map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>
                  {formatCustomerLabel(customer)}
                </MenuItem>
              ))}
            </Select>
            {error && <FormHelperText>{error}</FormHelperText>}
          </FormControl>

          <SoftBox mt={3}>
            <SoftTypography variant="button" fontWeight="medium" color="text" display="block" mb={0.5}>
              Endkundenpreis (optional):
            </SoftTypography>
            <SoftBox display="flex" alignItems="center" style={{ gap: 8 }}>
              <SoftBox style={{ maxWidth: 180 }}>
                <SoftInput
                  type="number"
                  value={endCustomerPrice}
                  onChange={(e) => setEndCustomerPrice(e.target.value)}
                  placeholder="z.B. 49.99"
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </SoftBox>
              <SoftTypography variant="button" fontWeight="regular" color="text">
                €/Monat
              </SoftTypography>
            </SoftBox>
            <SoftTypography variant="caption" color="secondary" display="block" mt={0.5}>
              Leer lassen = Kunden sehen den UDS-Standardpreis
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <SoftButton 
          onClick={onClose} 
          variant="outlined" 
          color="secondary"
          disabled={isLoading}
        >
          Abbrechen
        </SoftButton>
        <SoftButton 
          onClick={handleAssign} 
          variant="gradient" 
          color="info"
          disabled={isLoading || !selectedCustomerId}
        >
          {isLoading ? 'Wird zugewiesen...' : 'Zuweisen'}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
};

ServiceAssignDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  serviceId: PropTypes.number,
  serviceType: PropTypes.oneOf(['iaas', 'saas']).isRequired,
  onAssigned: PropTypes.func.isRequired
};

ServiceAssignDialog.defaultProps = {
  serviceId: null
};

export default ServiceAssignDialog;
